import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    FormControlLabel,
    FormGroup,
    styled,
    Switch,
    SwitchProps,
    Chip,
    Menu,
    MenuItem,
    Autocomplete,
    Tabs,
    Tab
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import * as React from "react";
import { Edit, Save, Warning } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { urls } from "@routes";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { format } from "date-fns";


interface CustomizedSwitchProps {
    checked: boolean;
    onChange: (value: boolean) => void;
}

export type FeatureToggleKey = 
    | "enable_contact_verification"
    | "master_inbox_enabled"
    | "non_campaign_emails_enabled"
    | "enable_ab_testing"
    | "enable_survey"
    | "enable_esp_sending"
    | "burnt_domain_pause_warmup"
    | "burnt_domain_pause_running_campaigns"
    | "burnt_domain_stop_future_campaigns"
    | "burnt_domain_team_alert_email"
    | "enable_domain_registrar"
    | "enable_onboarding_question"
    | "enable_api_v2"
    | "enable_api_v1_domain_contact_list"
    | "enable_google_postmaster"
    | "enable_manual_email"
    | "enable_hot_leads"
    | "enable_auto_responder";

export default function AdminFeatureFlags() {
    const { enqueueSnackbar } = useSnackbar();
    const [featureToggles, setFeatureToggles] = useState<Record<FeatureToggleKey, boolean>>({        
        enable_contact_verification: false, 
        master_inbox_enabled: false,
        non_campaign_emails_enabled: false,
        enable_ab_testing: false,
        enable_survey: false,
        enable_esp_sending: false,
        burnt_domain_pause_warmup: false,
        burnt_domain_pause_running_campaigns: false,
        burnt_domain_stop_future_campaigns: false,
        burnt_domain_team_alert_email: false,
        enable_manual_email: false,
        enable_domain_registrar: false,
        enable_onboarding_question: false,
        enable_api_v2: false,
        enable_api_v1_domain_contact_list: false,
        enable_google_postmaster: false,
        enable_hot_leads: false,
        enable_auto_responder: false,
    });

    const pageDataQuery = useQuery({
        queryKey: ["adminFeatureFlagSetting"],
        queryFn: () => authenticateAndFetchData("/admin/get-email-settings/"),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (!pageDataQuery.data) return;

        const data = pageDataQuery.data.data as Record<FeatureToggleKey, boolean>;

        setFeatureToggles(data);
    }, [pageDataQuery.data]);

    const FeatureSettingsMutation = useMutation({
        mutationKey: ["featureSettings"],
        mutationFn: async ({ key, value }: { key: FeatureToggleKey; value: boolean }) => {
            return authenticateAndPostData("/admin/email-settings/", {
                [key]: value,
            });
        },
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Successfully updated Feature Flag!", { variant: "success" });
            pageDataQuery.refetch();
        },
        onError: (error: ApiRequestFailed) => {
            enqueueSnackbar(error.data.message, { variant: "error" });
        },
    });

    const handleToggle = (key: FeatureToggleKey, value: boolean) => {
        setFeatureToggles(prev => ({
            ...prev,
            [key]: value,
        }));

        FeatureSettingsMutation.mutate({
            key,
            value,
        });
    };


    function CustomizedSwitch({ checked, onChange }: CustomizedSwitchProps) {
        const IOSSwitch = styled((props: SwitchProps) => (
            <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
        ))(({ theme }) => ({
            width: 42,
            height: 26,
            padding: 0,
            '& .MuiSwitch-switchBase': {
                padding: 0,
                margin: 2,
                transitionDuration: '300ms',
                '&.Mui-checked': {
                    transform: 'translateX(16px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                        backgroundColor: '#0288d1',
                        opacity: 1,
                        border: 0,
                        ...theme.applyStyles('dark', {
                            backgroundColor: '#0288d1',
                        }),
                    },
                    '&.Mui-disabled + .MuiSwitch-track': {
                        opacity: 0.5,
                    },
                },
                '&.Mui-focusVisible .MuiSwitch-thumb': {
                    color: '#0288d1',
                    border: '6px solid #fff',
                },
                '&.Mui-disabled .MuiSwitch-thumb': {
                    color: theme.palette.grey[100],
                    ...theme.applyStyles('dark', {
                        color: theme.palette.grey[600],
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.7,
                    ...theme.applyStyles('dark', {
                        opacity: 0.3,
                    }),
                },
            },
            '& .MuiSwitch-thumb': {
                boxSizing: 'border-box',
                width: 22,
                height: 22,
            },
            '& .MuiSwitch-track': {
                borderRadius: 26 / 2,
                backgroundColor: '#E9E9EA',
                opacity: 1,
                transition: theme.transitions.create(['background-color'], {
                    duration: 500,
                }),
                ...theme.applyStyles('dark', {
                    backgroundColor: '#39393D',
                }),
            },
        }));

        return (
            <FormGroup sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: '78px' }}>
                <FormControlLabel
                    control={<IOSSwitch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
                    label=""
                />
            </FormGroup>
        );
    }

    if (pageDataQuery.isLoading) {
        return (
            <PageLoading />
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />

    } else {
        return (
            <Stack direction="column" spacing={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4">Feature Flags</Typography>
                </Box>
                <Stack direction="column" spacing={2} sx={{ pb: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Control which features are visible and accessible to all users.
                    </Typography>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_contact_verification}
                            onChange={(value) => handleToggle("enable_contact_verification", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Contact List Verification
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.master_inbox_enabled}
                            onChange={(value) => handleToggle("master_inbox_enabled", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Master Inbox
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.non_campaign_emails_enabled}
                            onChange={(value) => handleToggle("non_campaign_emails_enabled", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Non-Campaign Emails in Master Inbox
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_ab_testing}
                            onChange={(value) => handleToggle("enable_ab_testing", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable A/B Testing
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_survey}
                            onChange={(value) => handleToggle("enable_survey", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Survey
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_esp_sending}
                            onChange={(value) => handleToggle("enable_esp_sending", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable ESP Sending
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_domain_registrar}
                            onChange={(value) => handleToggle("enable_domain_registrar", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Domain Registrar
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_onboarding_question}
                            onChange={(value) => handleToggle("enable_onboarding_question", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Onboarding Question
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_api_v2}
                            onChange={(value) => handleToggle("enable_api_v2", value)}
                        />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable Deliveryman API V2
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Turning this off makes every /api/v2/ endpoint return 503, hides API webhook
                                settings in the dashboard and shows an unavailable notice in the public API docs.
                                Emails already accepted still go out, and unsubscribe links keep working.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_api_v1_domain_contact_list}
                            onChange={(value) => handleToggle("enable_api_v1_domain_contact_list", value)}
                        />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Enable API V1 Domain & Contact List Endpoints
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Turning this off makes the /api/v1/domain/ and /api/v1/contact-list/ management
                                endpoints return 503 for non-admin users and shows a Coming Soon notice on their
                                pages in the public API docs. Admin accounts can always use them.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_google_postmaster}
                            onChange={(value) => handleToggle("enable_google_postmaster", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Google Postmaster
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_manual_email}
                            onChange={(value) => handleToggle("enable_manual_email", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Manual Email Sent Feature
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_hot_leads}
                            onChange={(value) => handleToggle("enable_hot_leads", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Hot leads
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.enable_auto_responder}
                            onChange={(value) => handleToggle("enable_auto_responder", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Enable Auto Responder
                        </Typography>
                    </Box>

                    <Divider />
                    <Typography variant="subtitle2" color="text.secondary">
                        A domain is flagged as burnt when a provider rejects it on sending-domain
                        reputation. Pausing happens the moment a burn is detected, so turning these on
                        later does not go back and pause domains already flagged.
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.burnt_domain_pause_warmup}
                            onChange={(value) => handleToggle("burnt_domain_pause_warmup", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Pause Warmup for Burnt Domains
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.burnt_domain_pause_running_campaigns}
                            onChange={(value) => handleToggle("burnt_domain_pause_running_campaigns", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Pause Running Campaigns for Burnt Domains
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.burnt_domain_stop_future_campaigns}
                            onChange={(value) => handleToggle("burnt_domain_stop_future_campaigns", value)}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            Stop Future Campaigns for Burnt Domains
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CustomizedSwitch
                            checked={featureToggles.burnt_domain_team_alert_email}
                            onChange={(value) => handleToggle("burnt_domain_team_alert_email", value)}
                        />
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                New Burnt Domain Notification
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Emails the team once when a domain is first flagged as burnt.
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
        )
    }
}